import React, { useEffect, useId, useRef, useState } from "react";

const CONFIRM_TEXT =
  "Проверьте, что оплата точно не прошла. Создать новую ссылку на оплату?";
const STEP_TEXT = {
  checking: "Проверяем, что по старой ссылке оплата не прошла…",
  creating: "Создаём новую ссылку…",
};
const CREATE_FAILED_TEXT = "Не удалось создать ссылку, попробуйте ещё раз";

function openPaymentPage(url) {
  window.location.assign(url);
}

/** Сообщение отказа сервера; сетевые сбои и CREATE_FAILED — общий текст. */
function refusalText(error) {
  if (error?.code && error.code !== "CREATE_FAILED" && error.message) {
    return error.message;
  }
  return CREATE_FAILED_TEXT;
}

/** Перерисовка в момент, когда наступит availableAt. */
function useReachedTime(isoTime) {
  const at = isoTime ? new Date(isoTime).getTime() : 0;
  const [, setTick] = useState(0);
  useEffect(() => {
    const wait = at - Date.now();
    if (!(wait > 0)) {
      return undefined;
    }
    const timerId = window.setTimeout(() => setTick((value) => value + 1), wait + 50);
    return () => window.clearTimeout(timerId);
  }, [at]);
  return !at || Date.now() >= at;
}

function PaymentRetryButton({
  paymentRetry,
  failed = false,
  onCheck,
  onCreate,
  onQrCode,
  onChanged,
  support = null,
  redirect = openPaymentPage,
  className = "mt-3 flex flex-col items-start gap-1.5",
}) {
  const [phase, setPhase] = useState("idle");
  const [message, setMessage] = useState(null);
  const mounted = useRef(true);
  const titleId = useId();
  const reached = useReachedTime(paymentRetry?.availableAt);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const busy = phase === "checking" || phase === "creating" || phase === "redirect";
  const dialogOpen = phase === "confirm" || busy;
  const showButton = Boolean(paymentRetry?.available && reached) || dialogOpen;
  const showExhausted = Boolean(paymentRetry?.exhausted && failed);

  const refuse = (error) => {
    if (!mounted.current) {
      return;
    }
    setPhase("idle");
    setMessage({
      text: refusalText(error),
      tone: error?.code === "PAID" ? "note" : "error",
    });
    if (onChanged) {
      onChanged();
    }
  };

  const handleConfirm = async () => {
    setPhase("checking");
    try {
      await onCheck();
    } catch (error) {
      refuse(error);
      return;
    }
    if (!mounted.current) {
      return;
    }

    setPhase("creating");
    let result;
    try {
      result = await onCreate();
    } catch (error) {
      refuse(error);
      return;
    }
    if (!mounted.current) {
      return;
    }

    if (result?.paymentUrl) {
      setPhase("redirect");
      redirect(result.paymentUrl);
      return;
    }
    if (result?.qrCode && onQrCode) {
      setPhase("idle");
      onQrCode(result.qrCode);
      if (onChanged) {
        onChanged();
      }
      return;
    }
    refuse(null);
  };

  if (!showButton && !showExhausted && !message) {
    return null;
  }

  return (
    <div className={className}>
      {showButton && paymentRetry?.cardAvailable ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Если не получилось оплатить, нажмите:
        </p>
      ) : null}
      {showButton ? (
        <button
          type="button"
          className="rounded-md border border-blue-500/60 px-3 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-500/10 disabled:opacity-60 dark:text-blue-300"
          disabled={busy}
          onClick={() => {
            setMessage(null);
            setPhase("confirm");
          }}
        >
          {paymentRetry?.cardAvailable
            ? "Оплатить картой или другим способом"
            : "Не получилось оплатить"}
        </button>
      ) : null}

      {message ? (
        <p
          role="alert"
          className={
            message.tone === "error"
              ? "text-sm text-red-600 dark:text-red-400"
              : "text-sm text-gray-700 dark:text-gray-300"
          }
        >
          {message.text}
        </p>
      ) : null}

      {showExhausted ? (
        <div className="text-sm text-gray-700 dark:text-gray-300">
          <p>Другие способы оплаты закончились</p>
          {support}
        </div>
      ) : null}

      {dialogOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="flex w-full max-w-sm flex-col gap-4 rounded-xl bg-white p-5 text-left text-gray-900 shadow-xl dark:bg-[#1a1a1a] dark:text-white">
            <p id={titleId} className="text-base font-medium">
              {CONFIRM_TEXT}
            </p>
            {busy ? (
              <p role="status" className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
                />
                {phase === "checking" ? STEP_TEXT.checking : STEP_TEXT.creating}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-300 disabled:opacity-60 dark:bg-gray-700 dark:text-white"
                disabled={busy}
                onClick={() => setPhase("idle")}
              >
                Отмена
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={busy}
                onClick={handleConfirm}
              >
                Да, создать
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PaymentRetryButton;
